import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import {
  FRECUENTES_TZ,
  TIMEZONE_GROUPS,
  getTimezoneLabel,
  isUnknownTimezone,
  timezoneMatchesSearch,
  warmTimezoneLabelsInBackground,
} from "@/lib/timezones";

interface TimezoneSelectProps {
  value: string;
  onChange: (tz: string) => void;
  disabled?: boolean;
}

interface RenderGroup {
  etiqueta: string;
  zonas: string[];
}

/**
 * Selector de zona horaria con búsqueda. Reemplaza el `<select>` nativo (417
 * zonas IANA son demasiadas para desplazarse a mano). Sin dependencias nuevas:
 * input de texto + lista desplegable propia, cierre al clicar fuera o Escape,
 * navegación con flechas + Enter.
 */
export default function TimezoneSelect({
  value,
  onChange,
  disabled = false,
}: TimezoneSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Precalienta la caché de etiquetas del resto de zonas en segundo plano
  // (idle) apenas se monta el selector, para que abrir el desplegable sin
  // filtro no tenga que calcular las ~400 etiquetas de golpe.
  useEffect(() => {
    warmTimezoneLabelsInBackground();
  }, []);

  // Cierra al clicar fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredGroups: RenderGroup[] = useMemo(() => {
    const grupos: RenderGroup[] = [];

    // Si el valor guardado no está en el catálogo (p.ej. un alias antiguo),
    // se ofrece siempre arriba del todo para no perderlo al editar.
    if (isUnknownTimezone(value) && timezoneMatchesSearch(value, query)) {
      grupos.push({ etiqueta: "Actual", zonas: [value] });
    }

    const frecuentesFiltradas = FRECUENTES_TZ.filter((tz) =>
      timezoneMatchesSearch(tz, query),
    );
    if (frecuentesFiltradas.length > 0) {
      grupos.push({ etiqueta: "Frecuentes", zonas: frecuentesFiltradas });
    }

    for (const grupo of TIMEZONE_GROUPS) {
      const zonasFiltradas = grupo.zonas.filter((tz) =>
        timezoneMatchesSearch(tz, query),
      );
      if (zonasFiltradas.length > 0) {
        grupos.push({ etiqueta: grupo.continente, zonas: zonasFiltradas });
      }
    }

    return grupos;
  }, [query, value]);

  const flatZones = useMemo(
    () => filteredGroups.flatMap((g) => g.zonas),
    [filteredGroups],
  );

  // La lista visible cambia con cada tecleo: vuelve a resaltar el primer resultado.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  // Mantiene visible la opción resaltada al navegar con flechas.
  useEffect(() => {
    const tz = flatZones[highlightedIndex];
    const el = tz ? optionRefs.current[tz] : null;
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, flatZones]);

  const openDropdown = () => {
    if (disabled) return;
    setIsOpen(true);
    setQuery("");
  };

  const selectTimezone = (tz: string) => {
    onChange(tz);
    setIsOpen(false);
    setQuery("");
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === "Escape") {
      setIsOpen(false);
      setQuery("");
      inputRef.current?.blur();
      return;
    }

    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        openDropdown();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, flatZones.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const tz = flatZones[highlightedIndex];
      if (tz) selectTimezone(tz);
    }
  };

  const displayedValue = isOpen ? query : getTimezoneLabel(value);

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Search
          size={15}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          value={displayedValue}
          placeholder="Buscar zona horaria…"
          onFocus={openDropdown}
          onClick={openDropdown}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-400"
        />
        <ChevronDown
          size={16}
          className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
          {/* Cabecera: recuerda la zona seleccionada mientras se busca */}
          <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-xs text-gray-500 truncate">
            Actual: <span className="text-gray-700">{getTimezoneLabel(value)}</span>
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            {flatZones.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                No se encontró ninguna zona horaria
              </div>
            ) : (
              filteredGroups.map((grupo) => (
                <div key={grupo.etiqueta}>
                  <div className="px-3 py-1 bg-gray-50 sticky top-0">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider truncate">
                      {grupo.etiqueta}
                    </p>
                  </div>
                  {grupo.zonas.map((tz) => {
                    const globalIndex = flatZones.indexOf(tz);
                    const isHighlighted = globalIndex === highlightedIndex;
                    const isSelected = tz === value;
                    return (
                      <button
                        key={tz}
                        type="button"
                        ref={(el) => {
                          optionRefs.current[tz] = el;
                        }}
                        role="option"
                        aria-selected={isSelected}
                        onMouseEnter={() => setHighlightedIndex(globalIndex)}
                        onClick={() => selectTimezone(tz)}
                        className={`w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-colors ${
                          isHighlighted ? "bg-primary-50" : "hover:bg-gray-50"
                        } ${isSelected ? "text-primary-700 font-medium" : "text-gray-700"}`}
                      >
                        <span className="flex-1 min-w-0 truncate">
                          {getTimezoneLabel(tz)}
                        </span>
                        {isSelected && (
                          <Check
                            size={14}
                            className="text-primary-600 flex-shrink-0"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
